import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm font-sans font-medium transition-all border border-transparent disabled:pointer-events-none disabled:opacity-50 [&>svg]:pointer-events-none [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--accent)] text-white border-[var(--accent)] hover:bg-[var(--accent-hover)]',
        secondary:
          'bg-[var(--bg-elevated)] text-[var(--text-primary)] border-[var(--border)] hover:bg-[var(--bg-hover)] hover:border-[var(--text-muted)]',
        ghost: 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
        danger:
          'bg-[var(--accent-red-subtle)] text-[var(--accent-red)] hover:bg-[var(--accent-red)] hover:text-white',
        success:
          'bg-[var(--accent-green-subtle)] text-[var(--accent-green)] hover:bg-[var(--accent-green)] hover:text-white',
        mailto:
          'bg-gradient-to-br from-[var(--accent)] to-[var(--accent-cyan)] text-white border-none',
      },
      size: {
        sm: 'px-2.5 py-1.5 text-xs [&>svg]:size-3.5',
        md: 'px-4 py-2 text-[13px] [&>svg]:size-4',
        icon: 'size-8 p-1.5 [&>svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
